import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, REFERRAL_CATEGORIES } from "@/lib/api";
import { toast } from "sonner";
import {
  Anchor, Compass, Heart, Sparkles, ArrowRight, ArrowLeft, Check, Users, Baby,
  MapPin, Phone, BookOpen, Share2, Globe,
} from "lucide-react";

const STEPS = [
  { key: "select", letter: "·", title: "Choose Care Seeker", subtitle: "Who are we walking alongside today?", icon: Users },
  { key: "A", letter: "A", title: "Acknowledge Safety", subtitle: "Begin by making space for safety.", icon: Anchor },
  { key: "N", letter: "N", title: "Navigate Immediate Needs", subtitle: "What needs are most pressing right now?", icon: Compass },
  { key: "C", letter: "C", title: "Connect to Resources", subtitle: "Which supports could ease the way forward?", icon: Sparkles },
  { key: "H", letter: "H", title: "Help with Grounding", subtitle: "Take a breath. How are they doing emotionally?", icon: Heart },
  { key: "O", letter: "O", title: "Outline Next Steps", subtitle: "What feels like the right next step?", icon: ArrowRight },
  { key: "M", letter: "·", title: "Maternal & Family Wellness", subtitle: "Optional — only if relevant.", icon: Baby },
  { key: "R", letter: "R", title: "Reconnect", subtitle: "When and how shall we follow up?", icon: Heart },
  { key: "review", letter: "✓", title: "Review & Submit", subtitle: "A gentle final look before saving.", icon: Check },
];

const IMMEDIATE_NEEDS = [
  "Food", "Shelter", "Transportation", "Childcare", "Medical care",
  "Mental health support", "Financial assistance", "Safety planning",
];

const EMOTIONAL_STATES = [
  { value: "calm", label: "Calm" },
  { value: "hopeful", label: "Hopeful" },
  { value: "stressed", label: "Stressed" },
  { value: "overwhelmed", label: "Overwhelmed" },
  { value: "grieving", label: "Grieving" },
  { value: "anxious", label: "Anxious" },
  { value: "neutral", label: "Neutral" },
];

export default function AnchorIntake() {
  const [searchParams] = useSearchParams();
  const initialCsId = searchParams.get("care_seeker_id") || "";
  const navigate = useNavigate();

  const [stepIdx, setStepIdx] = useState(initialCsId ? 1 : 0);
  const [careSeekers, setCareSeekers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    care_seeker_id: initialCsId,
    intake_type: "full",
    feels_safe: null, safety_concerns: "", safety_notes: "",
    immediate_needs: [], immediate_needs_notes: "",
    resources_needed: [], resource_notes: "",
    connected_resource_ids: [],
    emotional_state: "", grounding_notes: "", support_systems: "",
    next_steps: "", referrals_needed: [],
    maternal_relevant: false, pregnancy_status: "", postpartum_weeks: null, maternal_notes: "",
    follow_up_preference: "", follow_up_date: "",
  });

  useEffect(() => {
    api.get("/care-seekers").then(({ data }) => setCareSeekers(data)).catch(() => {});
  }, []);

  // Resources directory — loaded on demand the first time the user reaches
  // the "Connect to Resources" step. Stored once and filtered client-side
  // by the user's category/county selections (snappier than re-fetching).
  const [allResources, setAllResources] = useState(null);
  const [resourceFilter, setResourceFilter] = useState("any-county"); // "cs-county" | "any-county"

  const careSeeker = careSeekers.find((c) => c.id === data.care_seeker_id);

  useEffect(() => {
    if (currentStep?.key === "C" && allResources === null) {
      api
        .get("/resources")
        .then(({ data: items }) => setAllResources(items || []))
        .catch(() => setAllResources([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx]);

  const filteredResources = useMemo(() => {
    if (!allResources) return [];
    const cats = data.resources_needed || [];
    if (cats.length === 0) return [];
    const seekerCounty = (careSeeker?.county || "").trim().toLowerCase();
    return allResources
      .filter((r) => cats.includes(r.category))
      .filter((r) => {
        if (resourceFilter === "any-county") return true;
        if (!seekerCounty) return true;
        return (r.county || "").trim().toLowerCase() === seekerCounty;
      })
      .sort((a, b) => {
        // Care Seeker's county first, then alpha
        const ac = (a.county || "").toLowerCase() === seekerCounty ? 0 : 1;
        const bc = (b.county || "").toLowerCase() === seekerCounty ? 0 : 1;
        if (ac !== bc) return ac - bc;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [allResources, data.resources_needed, careSeeker, resourceFilter]);

  const createReferralFromResource = async (resource) => {
    if (!data.care_seeker_id) {
      toast.error("Pick a Care Seeker first");
      return;
    }
    try {
      await api.post("/referrals", {
        care_seeker_id: data.care_seeker_id,
        category: resource.category,
        organization: resource.name,
        contact_info: [resource.phone, resource.email, resource.website]
          .filter(Boolean)
          .join(" · "),
        referral_date: new Date().toISOString().slice(0, 10),
        status: "pending",
        notes: `Connected during A.N.C.H.O.R. intake. ${resource.description || ""}`.trim(),
      });
      toast.success(`Draft referral created for ${resource.name}`);
      // Auto-attach the resource if it wasn't already
      if (!data.connected_resource_ids.includes(resource.id)) {
        toggleArr("connected_resource_ids", resource.id);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const update = (k, v) => setData({ ...data, [k]: v });
  const toggleArr = (k, v) =>
    setData((d) => {
      const arr = d[k] || [];
      return { ...d, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
    });

  const visibleSteps = data.intake_type === "quick"
    ? STEPS.filter((s) => !["H", "M"].includes(s.key))
    : STEPS;
  const totalSteps = visibleSteps.length;
  const currentStep = visibleSteps[stepIdx];

  const canGoNext = () => {
    if (currentStep.key === "select") return !!data.care_seeker_id;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        postpartum_weeks: data.postpartum_weeks ? Number(data.postpartum_weeks) : null,
      };
      const { data: intake } = await api.post("/intakes", payload);
      toast.success("A.N.C.H.O.R. intake saved");
      navigate(`/care-seekers/${intake.care_seeker_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="anchor-intake-page">
      <div>
        <div className="hw-eyebrow mb-2">A guided conversation</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">A.N.C.H.O.R. Intake</h1>
        <p className="text-[#334155] mt-2">
          A warm, step-by-step way to gather what's needed — at the Care Seeker's pace.
        </p>
      </div>

      {/* Intake type */}
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex gap-2" data-testid="intake-type-toggle">
        {["quick", "full"].map((t) => (
          <button
            key={t}
            onClick={() => update("intake_type", t)}
            data-testid={`intake-type-${t}`}
            className={`flex-1 py-2.5 rounded-full text-sm font-medium capitalize transition-colors ${
              data.intake_type === t ? "bg-[#1E3A8A] text-white" : "bg-[#F9F8F6] text-[#334155]"
            }`}
          >
            {t} intake
          </button>
        ))}
      </div>

      {/* Progress */}
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5">
        <div className="flex items-center justify-between text-xs text-[#1E3A8A] mb-2">
          <span>Step {stepIdx + 1} of {totalSteps}</span>
          <span className="hw-eyebrow">{currentStep.title}</span>
        </div>
        <div className="h-1.5 bg-[#E8ECE7] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#7E937D] transition-all duration-300"
            style={{ width: `${((stepIdx + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* Step card */}
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center font-display font-bold text-2xl">
            {currentStep.letter}
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-[#0F172A]">{currentStep.title}</h2>
            <p className="text-sm text-[#1E3A8A]">{currentStep.subtitle}</p>
          </div>
        </div>

        {/* Step content */}
        {currentStep.key === "select" && (
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Care Seeker</label>
            <select
              data-testid="intake-care-seeker-select"
              value={data.care_seeker_id}
              onChange={(e) => update("care_seeker_id", e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
            >
              <option value="">Select a Care Seeker…</option>
              {careSeekers.map((cs) => (
                <option key={cs.id} value={cs.id}>
                  {cs.preferred_name || cs.first_name} {cs.last_name}
                </option>
              ))}
            </select>
            {careSeekers.length === 0 && (
              <p className="text-xs text-[#1E3A8A] mt-2">
                No Care Seekers yet. <a href="/care-seekers" className="text-[#1E3A8A] underline">Add one first</a>.
              </p>
            )}
          </div>
        )}

        {currentStep.key === "A" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Does the Care Seeker feel safe?</label>
              <div className="grid grid-cols-3 gap-2" data-testid="feels-safe-buttons">
                {[{v:true,l:"Yes"},{v:false,l:"No"},{v:null,l:"Unsure"}].map((opt) => (
                  <button
                    key={String(opt.v)}
                    onClick={() => update("feels_safe", opt.v)}
                    className={`py-2.5 rounded-xl border text-sm font-medium ${
                      data.feels_safe === opt.v
                        ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                        : "border-[#E5E5E0] text-[#334155]"
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            <Textarea label="Any safety concerns to acknowledge?" value={data.safety_concerns} onChange={(v) => update("safety_concerns", v)} testId="safety-concerns" placeholder="In their own words…" />
            <Textarea label="Staff notes" value={data.safety_notes} onChange={(v) => update("safety_notes", v)} testId="safety-notes" />
          </div>
        )}

        {currentStep.key === "N" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">What's most pressing right now?</label>
              <div className="flex flex-wrap gap-2" data-testid="immediate-needs">
                {IMMEDIATE_NEEDS.map((need) => (
                  <button
                    key={need}
                    onClick={() => toggleArr("immediate_needs", need)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                      data.immediate_needs.includes(need)
                        ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                        : "bg-white border-[#E5E5E0] text-[#334155]"
                    }`}
                  >
                    {need}
                  </button>
                ))}
              </div>
            </div>
            <Textarea label="Anything more to add" value={data.immediate_needs_notes} onChange={(v) => update("immediate_needs_notes", v)} testId="needs-notes" />
          </div>
        )}

        {currentStep.key === "C" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Which supports could help?</label>
              <div className="flex flex-wrap gap-2" data-testid="resource-categories">
                {REFERRAL_CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => toggleArr("resources_needed", c.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border ${
                      data.resources_needed.includes(c.value)
                        ? "bg-[#7E937D] text-white border-[#7E937D]"
                        : "bg-white border-[#E5E5E0] text-[#334155]"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Live picker from the Resources directory */}
            {data.resources_needed.length > 0 && (
              <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-2xl p-4" data-testid="resource-picker">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                  <div>
                    <div className="hw-eyebrow text-[10px] mb-1">From your Resources directory</div>
                    <div className="text-sm text-[#0F172A] font-medium">
                      Matching organizations
                      {careSeeker?.county && (
                        <span className="text-[#1E3A8A] font-normal">
                          {" "}· {careSeeker.county} County first
                        </span>
                      )}
                    </div>
                  </div>
                  {careSeeker?.county && (
                    <div className="inline-flex bg-white border border-[#E5E5E0] rounded-full p-1 text-xs">
                      <button
                        type="button"
                        onClick={() => setResourceFilter("cs-county")}
                        className={`px-3 py-1 rounded-full transition-colors ${
                          resourceFilter === "cs-county"
                            ? "bg-[#1E3A8A] text-white"
                            : "text-[#334155]"
                        }`}
                        data-testid="filter-cs-county"
                      >
                        {careSeeker.county} only
                      </button>
                      <button
                        type="button"
                        onClick={() => setResourceFilter("any-county")}
                        className={`px-3 py-1 rounded-full transition-colors ${
                          resourceFilter === "any-county"
                            ? "bg-[#1E3A8A] text-white"
                            : "text-[#334155]"
                        }`}
                        data-testid="filter-any-county"
                      >
                        All counties
                      </button>
                    </div>
                  )}
                </div>

                {allResources === null ? (
                  <div className="text-sm text-[#1E3A8A] py-4 text-center">Loading directory…</div>
                ) : filteredResources.length === 0 ? (
                  <div className="text-sm text-[#334155] py-6 text-center" data-testid="resource-picker-empty">
                    <BookOpen className="w-7 h-7 text-[#7E937D] mx-auto mb-2" strokeWidth={1.5} />
                    No matching resources in the directory yet.
                    <div className="text-xs mt-1 text-[#1E3A8A]">
                      Tip: add organizations under <span className="font-mono">Resources</span> so they appear here next time.
                    </div>
                  </div>
                ) : (
                  <ul className="space-y-2" data-testid="resource-picker-list">
                    {filteredResources.map((r) => {
                      const selected = data.connected_resource_ids.includes(r.id);
                      return (
                        <li
                          key={r.id}
                          data-testid={`resource-${r.id}`}
                          className={`bg-white rounded-xl border p-3 transition-colors ${
                            selected ? "border-[#7E937D] ring-1 ring-[#7E937D]" : "border-[#E5E5E0]"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <label className="flex items-start gap-3 flex-1 cursor-pointer min-w-0">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleArr("connected_resource_ids", r.id)}
                                className="mt-1 rounded"
                                data-testid={`resource-check-${r.id}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-[#0F172A] truncate">{r.name}</span>
                                  <span className="text-[10px] bg-[#E8ECE7] text-[#1E3A8A] px-2 py-0.5 rounded-full capitalize">
                                    {(r.category || "").replace(/_/g, " ")}
                                  </span>
                                  {r.county && (
                                    <span className="text-[10px] text-[#1E3A8A] inline-flex items-center gap-1">
                                      <MapPin className="w-3 h-3" strokeWidth={1.75} /> {r.county}
                                    </span>
                                  )}
                                </div>
                                {r.description && (
                                  <p className="text-xs text-[#334155] mt-1 line-clamp-2">{r.description}</p>
                                )}
                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-[#1E3A8A]">
                                  {r.phone && (
                                    <a
                                      href={`tel:${r.phone}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center gap-1 hover:underline"
                                    >
                                      <Phone className="w-3 h-3" strokeWidth={1.75} /> {r.phone}
                                    </a>
                                  )}
                                  {r.website && (
                                    <a
                                      href={r.website}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center gap-1 hover:underline truncate max-w-[200px]"
                                    >
                                      <Globe className="w-3 h-3" strokeWidth={1.75} /> Website
                                    </a>
                                  )}
                                </div>
                              </div>
                            </label>
                            <button
                              type="button"
                              onClick={() => createReferralFromResource(r)}
                              data-testid={`create-referral-${r.id}`}
                              className="flex-shrink-0 text-[11px] px-2.5 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] font-medium hover:bg-[#E8ECE7] inline-flex items-center gap-1"
                              title="Create a draft outbound referral for this Care Seeker"
                            >
                              <Share2 className="w-3 h-3" strokeWidth={1.75} /> Refer
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {data.connected_resource_ids.length > 0 && (
                  <div className="mt-3 text-xs text-[#166534]" data-testid="connected-count">
                    {data.connected_resource_ids.length} resource{data.connected_resource_ids.length > 1 ? "s" : ""} attached to this intake.
                  </div>
                )}
              </div>
            )}

            <Textarea label="Resource notes" value={data.resource_notes} onChange={(v) => update("resource_notes", v)} testId="resource-notes" />
          </div>
        )}

        {currentStep.key === "H" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">How are they feeling today?</label>
              <div className="flex flex-wrap gap-2" data-testid="emotional-state">
                {EMOTIONAL_STATES.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => update("emotional_state", s.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border ${
                      data.emotional_state === s.value
                        ? "bg-[#F59E0B] text-white border-[#F59E0B]"
                        : "bg-white border-[#E5E5E0] text-[#334155]"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <Textarea label="Grounding notes" value={data.grounding_notes} onChange={(v) => update("grounding_notes", v)} testId="grounding-notes" placeholder="What helps them feel grounded?" />
            <Textarea label="Who supports them?" value={data.support_systems} onChange={(v) => update("support_systems", v)} testId="support-systems" />
          </div>
        )}

        {currentStep.key === "O" && (
          <div className="space-y-4">
            <Textarea label="What feels like the right next step?" value={data.next_steps} onChange={(v) => update("next_steps", v)} testId="next-steps" />
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Which referrals to start?</label>
              <div className="flex flex-wrap gap-2" data-testid="referrals-needed">
                {REFERRAL_CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => toggleArr("referrals_needed", c.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border ${
                      data.referrals_needed.includes(c.value)
                        ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                        : "bg-white border-[#E5E5E0] text-[#334155]"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {currentStep.key === "M" && (
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-4 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]" data-testid="maternal-relevant-toggle">
              <input
                type="checkbox"
                checked={data.maternal_relevant}
                onChange={(e) => update("maternal_relevant", e.target.checked)}
              />
              <span className="text-sm text-[#334155]">Maternal or family wellness is relevant for this Care Seeker</span>
            </label>
            {data.maternal_relevant && (
              <>
                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-2">Pregnancy status</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {["pregnant", "postpartum", "trying", "other"].map((s) => (
                      <button
                        key={s}
                        onClick={() => update("pregnancy_status", s)}
                        className={`py-2.5 rounded-xl text-sm font-medium border capitalize ${
                          data.pregnancy_status === s ? "bg-[#7E937D] text-white border-[#7E937D]" : "border-[#E5E5E0] text-[#334155]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                {data.pregnancy_status === "postpartum" && (
                  <div>
                    <label className="block text-sm font-medium text-[#334155] mb-2">Weeks postpartum</label>
                    <input
                      type="number"
                      value={data.postpartum_weeks || ""}
                      onChange={(e) => update("postpartum_weeks", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
                    />
                  </div>
                )}
                <Textarea label="Maternal notes" value={data.maternal_notes} onChange={(v) => update("maternal_notes", v)} testId="maternal-notes" />
              </>
            )}
          </div>
        )}

        {currentStep.key === "R" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">How would they like us to reconnect?</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {["phone", "text", "email", "home_visit"].map((c) => (
                  <button
                    key={c}
                    onClick={() => update("follow_up_preference", c)}
                    className={`py-2.5 rounded-xl text-sm font-medium border capitalize ${
                      data.follow_up_preference === c ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "border-[#E5E5E0] text-[#334155]"
                    }`}
                  >
                    {c.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Follow-up date</label>
              <input
                type="date"
                value={data.follow_up_date}
                onChange={(e) => update("follow_up_date", e.target.value)}
                data-testid="follow-up-date"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
              />
            </div>
          </div>
        )}

        {currentStep.key === "review" && (
          <div className="space-y-3" data-testid="intake-review">
            <ReviewRow label="Care Seeker" value={careSeekers.find((c) => c.id === data.care_seeker_id)?.first_name + " " + careSeekers.find((c) => c.id === data.care_seeker_id)?.last_name} />
            <ReviewRow label="Intake type" value={data.intake_type} />
            <ReviewRow label="Feels safe" value={data.feels_safe === true ? "Yes" : data.feels_safe === false ? "No" : "Unsure"} />
            <ReviewRow label="Immediate needs" value={data.immediate_needs.join(", ") || "—"} />
            <ReviewRow label="Resources needed" value={data.resources_needed.join(", ") || "—"} />
            <ReviewRow
              label="Resources attached"
              value={
                data.connected_resource_ids.length === 0
                  ? "—"
                  : (allResources || [])
                      .filter((r) => data.connected_resource_ids.includes(r.id))
                      .map((r) => r.name)
                      .join(", ") || `${data.connected_resource_ids.length} selected`
              }
            />
            <ReviewRow label="Emotional state" value={data.emotional_state || "—"} />
            <ReviewRow label="Next steps" value={data.next_steps || "—"} />
            <ReviewRow label="Referrals to start" value={data.referrals_needed.join(", ") || "—"} />
            <ReviewRow label="Follow-up" value={`${data.follow_up_preference || "—"} ${data.follow_up_date ? "· " + data.follow_up_date : ""}`} />
          </div>
        )}

        {/* Nav */}
        <div className="flex justify-between gap-3 mt-8">
          <button
            onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
            disabled={stepIdx === 0}
            data-testid="intake-back-btn"
            className="px-5 py-2.5 rounded-full border border-[#E5E5E0] text-[#334155] font-medium inline-flex items-center gap-2 disabled:opacity-40"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {stepIdx === totalSteps - 1 ? (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="intake-submit-btn"
              className="px-6 py-2.5 rounded-full bg-[#1E3A8A] text-white font-medium inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Check className="w-4 h-4" /> {submitting ? "Saving…" : "Save Intake"}
            </button>
          ) : (
            <button
              onClick={() => canGoNext() && setStepIdx((i) => Math.min(totalSteps - 1, i + 1))}
              disabled={!canGoNext()}
              data-testid="intake-next-btn"
              className="px-6 py-2.5 rounded-full bg-[#1E3A8A] text-white font-medium inline-flex items-center gap-2 disabled:opacity-50"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Textarea = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="block text-sm font-medium text-[#334155] mb-2">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      rows={3}
      className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
    />
  </div>
);

const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-2 border-b border-[#F0EFEB] text-sm">
    <span className="text-[#1E3A8A]">{label}</span>
    <span className="text-[#0F172A] font-medium text-right">{value || "—"}</span>
  </div>
);
